import React from "react";
import type { Lang, TranslationKey } from "./dict";
import { dict } from "./dict";

type I18nContextValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey) => string;
  d: typeof dict;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

const STORAGE_KEY = "app_lang";

function getFromStorage(): Lang {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "eng" || raw === "spa" ? raw : "spa";
}

function getValueByKey(key: string): unknown {
  return key.split(".").reduce<unknown>((acc, part) => {
    if (acc && typeof acc === "object" && part in (acc as any)) {
      return (acc as any)[part];
    }
    return undefined;
  }, dict as unknown);
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = React.useState<Lang>(() => {
    try {
      return getFromStorage();
    } catch {
      return "spa";
    }
  });

  const setLang = React.useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // ignore
    }
  }, []);

  const t = React.useCallback(
    (key: TranslationKey) => {
      const node = getValueByKey(key);

      // expected leaf: { spa: string; eng: string }
      if (!node || typeof node !== "object") return String(key);

      const value = (node as any)[lang];
      return typeof value === "string" ? value : String(key);
    },
    [lang]
  );

  const value = React.useMemo(
    () => ({ lang, setLang, t, d: dict }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
